import type { Bundle, Communication } from '@medplum/fhirtypes';
import { useSubscription } from '@medplum/react-hooks';
import { useCallback, useRef, useState } from 'react';
import { MSG } from '../services/messagingFhir';

const isDev = import.meta.env.DEV;

export interface CommunicationSubscriptionCallbacks {
  /** Called when a new/updated Communication arrives via WebSocket. */
  onCommunicationEvent?: (communication: Communication) => void;
  /** Called on reconnect so consumers can refetch to catch missed events. */
  onReconnect?: () => void;
}

export interface UseCommunicationSubscriptionReturn {
  /** Whether the WebSocket is currently connected. */
  connected: boolean;
  /** Whether subscription is available (false if feature flag is off or errored). */
  available: boolean;
}

function communicationFromSubscriptionBundle(bundle: Bundle): Communication | undefined {
  for (const entry of bundle.entry ?? []) {
    const resource = entry.resource;
    if (resource?.resourceType === 'Communication') {
      return resource;
    }
  }
  return undefined;
}

/**
 * Subscribes to Communication resource events via Medplum's useSubscription hook.
 * Filters out non-messaging Communications (Rules Engine notifications).
 *
 * @param criteria - FHIR search criteria for the subscription (e.g. 'Communication?recipient=Practitioner/123').
 *   Pass undefined to temporarily unsubscribe.
 * @param callbacks - Event handlers for incoming Communications and reconnection.
 */
export function useCommunicationSubscription(
  criteria: string | undefined,
  callbacks: CommunicationSubscriptionCallbacks,
): UseCommunicationSubscriptionReturn {
  const [connected, setConnected] = useState(false);
  const [available, setAvailable] = useState(false);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const handleBundle = useCallback((bundle: Bundle) => {
    const communication = communicationFromSubscriptionBundle(bundle);
    if (!communication) return;

    if (isDev) {
      console.log(
        '[Messaging][WS] Received Communication',
        communication.resourceType + '/' + communication.id,
        'identifier:', communication.identifier?.map((i) => `${i.system}|${i.value}`).join(', '),
        'partOf:', communication.partOf?.[0]?.reference,
        'recipient:', communication.recipient?.map((r) => r.reference).join(', '),
      );
    }

    const isMessaging = communication.identifier?.some(
      (id) => id.system === MSG.IDENTIFIER_SYSTEM,
    );
    if (!isMessaging) {
      if (isDev) {
        console.log(
          '[Messaging][WS] Filtered out: missing Unify messaging identifier',
          communication.resourceType + '/' + communication.id,
        );
      }
      return;
    }

    callbacksRef.current.onCommunicationEvent?.(communication);
  }, []);

  const handleWebSocketOpen = useCallback(() => {
    if (isDev) {
      const scope = criteria?.includes('part-of=') ? 'thread-scoped' : 'user-scoped';
      console.log(`[Messaging][WS] Connected (${scope}) — criteria:`, criteria);
    }
    setConnected(true);
    setAvailable(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria]);

  const handleWebSocketClose = useCallback(() => {
    if (isDev) {
      console.log('[Messaging][WS] Disconnected');
    }
    setConnected(false);
  }, []);

  const handleSubscriptionConnect = useCallback(() => {
    if (isDev) {
      const scope = criteria?.includes('part-of=') ? 'thread-scoped' : 'user-scoped';
      console.log(`[Messaging][WS] Subscription connected/reconnect (${scope}) — criteria:`, criteria);
    }
    callbacksRef.current.onReconnect?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria]);

  const handleError = useCallback((err: Error) => {
    console.warn('[Messaging] WebSocket subscription error:', err.message);
    setConnected(false);
    setAvailable(false);
  }, []);

  useSubscription(
    criteria,
    handleBundle,
    {
      onWebSocketOpen: handleWebSocketOpen,
      onWebSocketClose: handleWebSocketClose,
      onSubscriptionConnect: handleSubscriptionConnect,
      onError: handleError,
    },
  );

  return { connected, available };
}
